import { css } from "@emotion/react";
import { Fragment, useCallback, useEffect, useRef } from "react";

import type JoinSession from "@mpa/toolbox/models/JoinSession";

import Button from "../../../components/Button/Button";
import Centered from "../../../components/Centered";
import CountdownProgress from "../../../components/CountdownProgress";
import Spacer from "../../../components/space/Spacer";
import { useStepperContext } from "../../../components/Stepper/constants";
import Text from "../../../components/Text";
import {
    callOutgoingRequest,
    refreshOutgoingRequest,
    useOutgoingRequest,
} from "../../../data/tanstack-query";
import { getJoinSession, invalidateJoinSession } from "../../../endpoints/outgoing-requests";
import { JoinSessionStatus } from "../../../enums/JoinSessionStatus";
import { navigate } from "../../../navigation/location";
import { contactsRoute } from "../navigation";

const codeCss = css({
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
    fontSize: "2.5rem",
    letterSpacing: "0.08em",
    textAlign: "center",
});

function formatJoinSession(code: string) {
    return `${code.slice(0, 3)}-${code.slice(3, 6)}-${code.slice(6, 9)}`;
}

type CodeStepProps = {
    session: JoinSession;
};

export default function CodeStep(props: CodeStepProps) {
    const { session } = props;
    const { goForward } = useStepperContext();
    const sessionCheck = useOutgoingRequest(getJoinSession(session.id));
    const formatted = formatJoinSession(session.code);
    const leaving = useRef(false);
    const advanced = useRef(false);
    const statusRef = useRef(sessionCheck.status);

    useEffect(() => {
        statusRef.current = sessionCheck.status;
    }, [sessionCheck.status]);

    const invalidateAndLeave = useCallback(async () => {
        if (leaving.current) return;
        if (statusRef.current === JoinSessionStatus.Consumed) return;
        leaving.current = true;
        try {
            const next = await callOutgoingRequest(invalidateJoinSession(session.id));
            if (next.status === JoinSessionStatus.Consumed) {
                leaving.current = false;
                await refreshOutgoingRequest(getJoinSession(session.id));
                return;
            }
        } catch {
            // L'invitation est déjà close.
        }
        navigate(contactsRoute.getLocation());
    }, [session.id]);

    useEffect(() => {
        if (sessionCheck.status !== JoinSessionStatus.Pending) return;
        const interval = window.setInterval(() => {
            void refreshOutgoingRequest(getJoinSession(session.id));
        }, 2000);
        return () => window.clearInterval(interval);
    }, [session.id, sessionCheck.status]);

    useEffect(() => {
        if (sessionCheck.status !== JoinSessionStatus.Consumed) return;
        if (advanced.current) return;
        advanced.current = true;
        goForward();
    }, [sessionCheck.status, goForward]);

    useEffect(() => {
        if (sessionCheck.status === JoinSessionStatus.Expired) void invalidateAndLeave();
        if (sessionCheck.status === JoinSessionStatus.Invalidated) {
            navigate(contactsRoute.getLocation());
        }
    }, [sessionCheck.status, invalidateAndLeave]);

    return (
        <Fragment>
            <Text variant="body">
                Communiquez ce code à votre partenaire. Il reste valable 15 minutes.
            </Text>
            <Spacer scale={6} />
            <div css={codeCss}>{formatted}</div>
            <Spacer scale={4} />
            <CountdownProgress
                from={session.createdAt}
                to={session.expiresAt}
                onComplete={() => void invalidateAndLeave()}
            />
            <Spacer scale={6} />
            <Centered>
                <Button
                    label="Copier"
                    role="side"
                    action={() => navigator.clipboard.writeText(formatted)}
                />
            </Centered>
            <Spacer scale={4} />
            <Centered>
                <Button
                    label="Annuler"
                    feeling="minor"
                    role="minor"
                    action={() => void invalidateAndLeave()}
                />
            </Centered>
        </Fragment>
    );
}
